/* eslint-disable arrow-body-style */
import * as React from "react";
import {autorun, reaction} from "mobx";
import {observer} from "mobx-react";
import {Header, Menu, Tab} from "semantic-ui-react";

import "./project-perimeter-editor.scss";

import {AppState} from "../../app-mobx";
import {MapState} from "../../../../core/map-state";

import {ApiLayerConfiguration} from "../../../../config/auftragsverwaltung-api-configuration";
import {Auftragsverwaltung} from "../../../../stores/auftragsverwaltung-mobx";

import {UiState} from "../../../../core/ui-state";
import {EditorConfiguration} from "../../../../core/config/editor-configuration";

import Handles from "@arcgis/core/core/Handles";
import {MapViewerConfiguration} from "../../../../config/map-viewer-configuration";


import {Icon} from "../../../../core/components/icon/icon";

import {SemanticShorthandItem} from "semantic-ui-react/dist/commonjs/generic";
import {TabPaneProps} from "semantic-ui-react/dist/commonjs/modules/Tab/TabPane";
import {ParcelWizard} from "./parcel-wizard/parcel-wizard";
import {FeatureConstructor} from "./feature-constructor/feature-constructor";
import {MunicipalityWizard} from "./municipality-wizard/municipality-wizard";


export enum EditorMode {
    FeatureTemplates = "featureTemplates",
    Municipalities = "municipalities",
    Parcels = "parcels"
}

export interface ActivityFeatureEditorProps {
    appStore: AppState;
    uiStore: UiState;
    config: EditorConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    mapStore: MapState;
    keyAppend?: string | number | undefined;
    layerInfos?: any;
    small?: boolean;
    auftragsverwaltung: Auftragsverwaltung;
    layers: ApiLayerConfiguration[];
}

/**
 * Filter features belonging to selected project
 */
@observer
export class ProjectPerimeterEditor extends React.Component<ActivityFeatureEditorProps, any> {
    watchHandles: any[] = [];
    sketchWatchHandle: any;
    autorunDisposers: any[] = [];
    handles: Handles = new Handles();
    widgetKey = "project-perimeter-editor";
    editorRef: any;

    constructor(props: ActivityFeatureEditorProps) {
        super(props);
        this.state = {
            activeIndex: 0,
            postMessages: [],
            preMessages: [],
            visible: true,
            isLoading: false,
            version: 0,
            perimeter: null,  // __esri.Graphic[]
            editor: null,  // editor
            viewModel: null,  // editor viewModel
            mode: EditorMode.FeatureTemplates
        };
        if (typeof this.props.keyAppend === "string" || typeof this.props.keyAppend === "number") {
            this.widgetKey += "-" + this.props.keyAppend.toString();
        }
        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
        }
        this.editorRef = React.createRef();

        this.handleTabChange = this.handleTabChange.bind(this);
        this.handleEsc = this.handleEsc.bind(this);
    }

    private get canStopEditing(): boolean {
        const {auftragsverwaltung} = this.props;
        const {editor, viewModel} = this.state;

        // TODO: Abbruchregeln festlegen
        if (editor?.activeWorkflow?.started &&
            (viewModel.state === "editing-new-feature" ||
                viewModel.state === "editing-existing-feature" ||
                viewModel.state === "creating-features")) {
            if (confirm("Nicht gespeicherte Änderungen. Änderungen verwerfen?")) {
                return true;
            } else {
                // reactivate editing
                auftragsverwaltung.setEditing(true, "project");
            }
            return false;
        } else {
            return true;
        }
    }

    componentDidMount(): void {
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((loadingReaction: any) => {
            const {auftragsverwaltung, mapStore, uiStore} = this.props;

            if (!auftragsverwaltung.selectedProject || !auftragsverwaltung.apiLoaded) {
                return null;
            }

            if (uiStore.widgets.has(this.widgetKey)) {
                loadingReaction.dispose(); // stop autorun
                return;
            }
            if (!mapStore.map || !mapStore.view) {
                return;
            }
            loadingReaction.dispose();

            this.setState({
                isLoading: false
            });

            // disable popup
            uiStore.setPopupDisabled(true);

            // watch changes in application edit mode
            this.autorunDisposers.push(reaction(
                () => auftragsverwaltung.isEditingProject,
                (isEditingActivity) => {
                    if (isEditingActivity) {
                        this.stopEditing();
                    }
                }
            ));

            // any proposals for perimeter (e.g. municipality selections)
            // this.autorunDisposers.push(autorun(() => {
            //     if(auftragsverwaltung.ActivityPerimeterStore?.perimeterProposal) {
            //         this.handleActivityProposal();
            //     }
            // }));

            uiStore.addWidget(this.widgetKey, this);

            // // activate parcel layer in map
            // // used for snapping
            // if (auftragsverwaltung.ParcelStore?.viewLayerView) {
            //     auftragsverwaltung.ParcelStore.viewLayerView.visible = true;
            // }

            // add ESC key handler
            document.addEventListener("keydown", this.handleEsc, false);
        }));
    }

    stopEditing(): void {
        if (this.canStopEditing) {
            this.cancelEditing();
        }
    }

    componentDidUpdate() {}

    componentWillUnmount(): void {
        const {uiStore} = this.props;

        this.cancelEditing();

        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        // remove sketch watch handle
        this.sketchWatchHandle?.remove();
        this.autorunDisposers.forEach((dispose: any) => {
            dispose();
        });
        this.autorunDisposers.length = 0;
        this.handles.removeAll();

        // remove esc handler
        document.removeEventListener("keydown", this.handleEsc, false);

        // enable popup
        uiStore.setPopupDisabled(false);

        uiStore.removeWidget(this.widgetKey);
    }

    render() {
        const {appStore, auftragsverwaltung, mapViewerConfig, mapStore, uiStore} = this.props;
        const {activeIndex, postMessages, preMessages, mode} = this.state;
        const project = auftragsverwaltung.selectedProject;

        const panes: Array<{
            pane?: SemanticShorthandItem<TabPaneProps>;
            menuItem?: any;
            render?: () => React.ReactNode;
        }> = [];

        // edit features with FeatureTemplates editor
        panes.push(
            {
                menuItem: (
                    <Menu.Item key="feature-templates">
                        <Icon name="pencil" />
                    </Menu.Item>
                ),
                render: () =>
                    <Tab.Pane attached={false} key="featureTemplates" className="feature-templates-tab">
                        {mode === EditorMode.FeatureTemplates &&
                            <>
                                <Header size="tiny" dividing>
                                    Objekte erzeugen oder ändern
                                </Header>
                                <FeatureConstructor
                                    auftragsverwaltung={auftragsverwaltung}
                                    mapViewerConfig={mapViewerConfig}
                                    project={project}
                                    mapStore={mapStore}
                                    uiStore={uiStore}
                                    appStore={appStore} />
                            </>
                        }
                    </Tab.Pane>
            }
        );

        // update edits
        panes.push(
            {
                menuItem: (
                    <Menu.Item key="copy-municipalities">
                        <Icon name="documents_empty" />
                    </Menu.Item>
                ),
                render: () =>
                    <Tab.Pane attached={false} key="copyMunicipalities" className="feature-copy-tab">
                        <Header size="tiny" dividing>
                            Gemeinde als Perimeter übernehmen
                        </Header>
                        {mode === EditorMode.Municipalities &&
                            <MunicipalityWizard
                                auftragsverwaltung={auftragsverwaltung}
                                mapViewerConfig={mapViewerConfig}
                                project={project}
                                mapStore={mapStore}
                                uiStore={uiStore}
                                appStore={appStore} />
                        }
                    </Tab.Pane>
            }
        );

        // edit perimeter by selecting parcels
        panes.push(
            {
                menuItem: (
                    <Menu.Item key="select-parcels">
                        <Icon name="magic_wand" />
                    </Menu.Item>
                ),
                render: () =>
                    <Tab.Pane attached={false} key="parcels">
                        <Header size="tiny" dividing>
                            Perimeter aus Parzellen zusammenstellen
                        </Header>
                        {mode === EditorMode.Parcels &&
                            <ParcelWizard
                                auftragsverwaltung={auftragsverwaltung}
                                mapViewerConfig={mapViewerConfig}
                                project={project}
                                mapStore={mapStore}
                                uiStore={uiStore}
                                appStore={appStore} />
                        }
                    </Tab.Pane>
            }
        );

        return (
            <div className={"geo-project-perimeter-editor"}>
                {preMessages}
                <Tab
                    menu={{ pointing: true }}
                    panes={panes}
                    activeIndex={activeIndex}
                    onTabChange={this.handleTabChange}
                />
                {postMessages}
                <div className="geo-project-perimeter-editor-footer"/>
            </div>
        );
    }

    private handleTabChange(e, { activeIndex }): void {
        const mode = this.setEditorMode(activeIndex);
        this.setState({ activeIndex, mode });
    }

    private handleEsc(event): void {
        if (event.key === "Escape") {
            this.stopEditing();
        }
        if (event.key === "+") {  }
    }

    // private handleActivityProposal(): void {
    //     const {auftragsverwaltung} = this.props;
    //     const {editor} = this.state;
    //
    //     // TODO: check project already has a perimeter
    //     if (!editor?.activeWorkFlow) {
    //         // TODO: find layer type based on selected project
    //         // create feature from template
    //         auftragsverwaltung.ActivityPerimeterStore.newPerimeter().then((perimeter) => {
    //             this.startFeatureCreation(perimeter);
    //         });
    //     }
    // }

    // private startFeatureCreation(feature: __esri.Graphic): void {
    //     const {mapViewerConfig, uiStore} = this.props;
    //     const {editor} = this.state;
    //
    //     // editor.startUpdateWorkflowAtFeatureEdit(
    //     //     perimeter
    //     // );
    //     feature.symbol = mapViewerConfig.symbols["perimeterProposal"] as __esri.Symbol;
    //
    //     const layer = feature.layer as __esri.FeatureLayer;
    //     // TODO: find out, how to get symbol from FeatureTemplate, FeatureType or Renderer
    //     // see 3.x sample (not working for 4.x)
    //     // https://community.esri.com/t5/net-maps-sdk-questions/get-symbol-for-a-featuretemplate/m-p/27219
    //     // const featureType = layer.getFeatureType(perimeter);
    //     const creationInfo = {
    //         layer,
    //         template: layer.templates[0]
    //     };
    //     // cancel previously active workflows
    //     if (editor.activeWorkflow) {
    //         editor.cancelWorkflow();
    //     }
    //     // start creation workflow
    //     editor.startCreateFeaturesWorkflowAtFeatureCreation(creationInfo).then(() => {
    //         // add feature as pending feature
    //         this.addFeatureAsSketch(feature);
    //         uiStore?.setPopupVisible(false);
    //     });
    // }

    /**
     * Add feature to the pending features of the active workflow
     * Removes all pending features before
     * @param feature
     * @private
     */
    // private addFeatureAsSketch(feature: __esri.Graphic): void {
    //     const {editor} = this.state;
    //     const {userStore, project} = this.props;
    //     const user = userStore.esriCredential?.userId || "";
    //
    //     const activityGuidFieldName = ActivityPerimeter.databaseFieldNames.get("activityGuid") || "";
    //     const creatorFieldName = ActivityPerimeter.databaseFieldNames.get("creator") || "";
    //     feature.attributes[activityGuidFieldName] =  project?.guid;
    //     feature.attributes[creatorFieldName] = user;
    //
    //     const workflow = editor?.activeWorkflow;
    //     if (workflow) {
    //         // remove previously added features from workflow
    //         workflow.pendingFeatures.removeAll();
    //         // add feature as pending feature
    //         workflow.pendingFeatures.add(feature);
    //         // move the pending feature into update mode
    //         workflow.updatePendingFeature(feature);
    //     }
    // }

    private cancelEditing(): void {
        const {auftragsverwaltung} = this.props;
        // const {viewModel} = this.state;
        // // clear the graphics when editing is stopped
        // if (viewModel && !auftragsverwaltung.isEditingActivity) {
        //     viewModel.activeWorkflow?.cancel();
        //     // TODO: clear perimeterProposal?
        // }
        auftragsverwaltung.setEditing(false);
    }

    private setEditorMode(tabIndex: number): EditorMode {
        const {mapStore} = this.props;

        // close popups
        mapStore.view?.closePopup();

        return this.getEditorModeByTabIndex(tabIndex);
    }

    private getEditorModeByTabIndex(tabIndex: number): EditorMode {
        if (tabIndex === 0) {
            return EditorMode.FeatureTemplates;
        }
        if (tabIndex === 1) {
            return EditorMode.Municipalities;
        }
        if (tabIndex === 2) {
            return EditorMode.Parcels;
        }
        return EditorMode.FeatureTemplates;
    }
}
